import * as React from 'react';
import { Refresh, Search, Share, Info } from '@mui/icons-material';
import { Autocomplete, Box, Chip, IconButton, LinearProgress, Stack, Tooltip, Link, createFilterOptions } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import { grey } from '@mui/material/colors';

import { Contexte, useAuthenticationValue } from '@lectra/react-authentication';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppStore } from '../appHooks';
import { CopyToClipboard } from '../common/CopyToClipBoard';
import { useGetSearchKeywordsQuery } from '../redux/appContext';
import { SearchParameters, minimalSearchStringLength, setSearch, useGetCutterByStringQuery, cutterSearchApi } from '../redux/cutterSearchAPI';
import { RootState } from '../store';
import CutterCardList from './CutterCardList';
import HelpIcon from '../help/HelpIcon';
import HelpTooltip from '../help/HelpTooltip';


const filterOptions = createFilterOptions({
    ignoreCase: true,
    matchFrom: "start",
    limit: 30,
});

const CuttersSearch: React.FC = () => {
    // user
    const user: Contexte = useAuthenticationValue();

    // search
    const txtSearchTextFieldRef = React.useRef<TextFieldProps>(undefined);
    const search: SearchParameters = useAppSelector((state: RootState) => state.persistedSearchReducer);
    const currentSearch = search.recentSearches?.[0] ?? '';
    const previousSearches = (search.recentSearches ?? []).slice(1, 4);

    const store = useAppStore();
    const [searchStr, setSearchStr] = React.useState(currentSearch);
    const [autoCompleteOpened, setAutoCompleteOpened] = React.useState(false);

    // navigation
    const location = useLocation();

    // Global App data : Search Keywords for Autocomplete search
    const searchKeywords: ReturnType<typeof useGetSearchKeywordsQuery> = useGetSearchKeywordsQuery();

    // Updates local state only while typing — does NOT push to recent searches stack
    const searchChanged: any = (searchTxt: string) => {
        setSearchStr(searchTxt);
    }

    // Commits a search to the persisted recent-searches stack (blur, Enter, chip click, URL nav)
    const commitSearch = (searchTxt: string): void => {
        if (!searchTxt || searchTxt.trim() === '') return;
        setSearchStr(searchTxt);
        store.dispatch(setSearch(searchTxt));
    }

    React.useEffect(() => {
        // execute on location change
        const queryParams: URLSearchParams = new URLSearchParams(location.search);
        if (queryParams.has('searchstring')) {
            commitSearch(queryParams.get('searchstring')!);
        }
    }, [location]);
    /* SEARCH RESULTS */

    const results: ReturnType<typeof useGetCutterByStringQuery> = useGetCutterByStringQuery(searchStr, {
        skip: autoCompleteOpened || user.accessToken === '' || (searchStr.length ?? 0) < minimalSearchStringLength
    });

    const selectedValues: string[] = React.useMemo(
        () => searchKeywords.data ? searchKeywords.data : [],
        [searchKeywords.status],
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* ----------------------------------------------- */}
            {/*                     SEARCH BAR                  */}
            {/* ----------------------------------------------- */}
            <AppBar position="static"
                color="default"
                elevation={0}
                sx={{
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525',
                    marginY: 0,
                    minHeight: 0
                }}>
                <Toolbar disableGutters sx={{ justifyContent: 'space-between', paddingX: '1em' }} >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: '3em' }}>
                        <Search sx={{ marginLeft: '1em' }} />
                        <HelpIcon helpContext="search-input" placement="right" />
                    </Stack>
                    <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
                        <Autocomplete freeSolo filterOptions={filterOptions}
                            sx={{
                                flex: 1,
                                backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[200] : grey[900],
                                marginY: -5,
                                borderRadius: 2,
                                paddingX: 1,
                                '& .MuiOutlinedInput-root': { paddingRight: '0 !important' }
                            }}
                            options={selectedValues} defaultValue={currentSearch} open={autoCompleteOpened}
                            onOpen={e => setAutoCompleteOpened(true)} onClose={(e, r) => setAutoCompleteOpened(false)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { setAutoCompleteOpened(false); commitSearch(txtSearchTextFieldRef.current?.value as string); }
                                if (e.key === 'Delete') { txtSearchTextFieldRef.current!.value = '' }
                            }}
                            value={searchStr} onBlur={event => commitSearch(txtSearchTextFieldRef.current?.value as string)}
                            onInputChange={(event, inputValue) => searchChanged(inputValue)}
                            renderInput={(params) =>
                                <TextField {...params} id="txt_search" inputRef={txtSearchTextFieldRef}
                                    placeholder={`First ${minimalSearchStringLength.toString()} chars of a serial number, company, machine name or model, country, software versions...`}
                                    slotProps={{ input: { ...params.InputProps, disableUnderline: true } }}
                                    variant="standard" data-tut="tabSearch_Txt"
                                />
                            } />
                        {previousSearches.map((s, i) => (
                            <Tooltip key={i} title={s.length > 50 ? s : ''} placement="bottom" arrow>
                                <Chip size="small"
                                    label={s.length > 50 ? s.substring(0, 47) + '...' : s}
                                    onClick={() => commitSearch(s)}
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[200] : grey[800],
                                        color: 'text.primary',
                                        '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[300] : grey[700] },
                                        flexShrink: 0,
                                        maxWidth: 160,
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center">
                        <Stack direction="row" alignItems="center">
                            <IconButton data-tut="tabSearch_Btn" title="Reload" onClick={e =>
                                store.dispatch(cutterSearchApi.util.invalidateTags(["CutterState"]))
                            }>
                                <Refresh color="inherit" />
                            </IconButton>
                            <HelpIcon helpContext="search-refresh" placement="left" />
                        </Stack>
                        <CopyToClipboard
                            TextToCopy={window.location.origin + `/search?searchstring=${encodeURI(currentSearch)}`} title='Copy to clipboard'
                            AckMessage={'Search link copied to clipboard'}>
                            <Share sx={{ color: 'default', padding: 0 }} titleAccess="Copy search results for sharing" />
                        </CopyToClipboard>
                        <HelpTooltip
                            title={
                                <Box sx={{ textAlign: 'justify', maxWidth: 400 }}>
                                    Your usage of this application is temporarily recorded for improvements, support, feature design, and prioritization.
                                    There are <u>no other uses</u>. No personal data is recorded except your email address.
                                    If you need more details, please contact:<br /> <Link href="mailto:DL_RD.Produce.CfactoryDev@lectra.com?subject=Device connections webapp > Privacy" color="inherit">DL_RD.Produce.CfactoryDev@lectra.com</Link>.
                                </Box>
                            }
                        >
                            <IconButton size="small" color="info">
                                <Info />
                            </IconButton>
                        </HelpTooltip>
                    </Stack>
                </Toolbar>
            </AppBar>

            {/* ----------------------------------------------- */}
            {/*                  SEARCH RESULTS                 */}
            {/* ----------------------------------------------- */}
            <LinearProgress color='secondary' sx={{ display: results.isFetching ? 'block' : 'none' }} />


            {/*      SEARCH RESULTS : Results                */}
            <CutterCardList container component={Stack} searchStr={searchStr} direction='row'
                sx={{ display: results.data ? 'flex-start' : 'none' }} />
        </Box>
    );
}

export default CuttersSearch;





